import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { suppliesListOrderAsk } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { OrderWithNancy } from "@/components/app/clinical/order-with-nancy";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Copy, Plus, Printer } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useKitchen } from "@/routes/_authenticated/menus/-lib/use-kitchen";
import { ListLineDialog } from "./-components/list-line-dialog";
import { ListLinesTable } from "./-components/list-lines-table";
import { LowSuggestions } from "./-components/low-suggestions";
import { printSupplyList } from "./-lib/print-list";

export const Route = createFileRoute("/_authenticated/supplies/list")({
  component: SuppliesListPage,
});

const today = () =>
  new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

/** The building's running supplies list: on it until Nancy orders it, with the closet's low items
    one press away. */
function SuppliesListPage() {
  const { facilityId, canManage } = useKitchen();
  const list = useQuery(orpc.agents.supplies.list.get.queryOptions({ input: { facilityId } }));
  const lines = list.data?.lines ?? [];

  const copy = async () => {
    if (!list.data?.text) return;
    try {
      await navigator.clipboard.writeText(list.data.text);
      toast.success("Copied the list");
    } catch {
      toast.error("Couldn't copy the list. Select it and copy it by hand.");
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {canManage ? (
          <ListLineDialog
            facilityId={facilityId}
            trigger={
              <Button size="sm">
                <Plus className="size-3.5" />
                Add item
              </Button>
            }
          />
        ) : null}
        <Button
          variant="outline"
          size="sm"
          disabled={lines.length === 0}
          onClick={() => void copy()}
        >
          <Copy className="size-3.5" />
          Copy list
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={lines.length === 0}
          onClick={() => printSupplyList(list.data?.building ?? "", list.data?.text ?? "", today())}
        >
          <Printer className="size-3.5" />
          Print list
        </Button>
        <OrderWithNancy
          facilityId={facilityId}
          askFor={(building) => suppliesListOrderAsk(building ?? list.data?.building)}
          disabled={list.isPending || lines.length === 0}
        />
      </div>

      {list.isPending ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : list.isError ? (
        <ReadFailed
          message="Couldn't load the supplies list."
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      ) : (
        <div className="space-y-6">
          {list.data.suggestions.length > 0 ? (
            <LowSuggestions
              facilityId={facilityId}
              canManage={canManage}
              suggestions={list.data.suggestions}
            />
          ) : null}
          {lines.length > 0 ? (
            <ListLinesTable facilityId={facilityId} canManage={canManage} lines={lines} />
          ) : (
            <EmptyState
              title="The supplies list is empty"
              description={
                canManage
                  ? "Add what the building needs, or ask the Kitchen agent to build it. Closet items below their minimum show up here to add."
                  : "Nothing is waiting to be bought for this building."
              }
            />
          )}
        </div>
      )}
    </section>
  );
}
